import { FORCE_CURRENT_USER } from '../core/engine/entity-registry'
import type { EntityRegistry } from '../core/engine/types'
import { DataClassName } from '@/Data/const'

// ─── Registry ─────────────────────────────────────────────────────────────────
//
// The concrete NeoCRM entity registry. This is per-host CONNECTOR data — it names
// NeoCRM entities via DataClassName — and is injected into the core through
// HostRuntime at bootstrap (see host/scrivito/useAiChatBotEnabled.ts). The core's
// generic tools never import this value; they read it via getEntityRegistry(). The
// registry SHAPE and the write-ops helpers live in the host-neutral core
// (engine/types.ts, engine/entity-registry.ts).

export const ENTITY_REGISTRY_METADATA: EntityRegistry = {
  [DataClassName.COMPANY]: {
    aliases: ['customer', 'account'],
    searchable: true,
    defaultOrder: [['updatedAt', 'desc']],
    write: { create: true, update: true },

    relations: {
      activities: {
        dataClass: DataClassName.ACTIVITY,
        viaJunction: {
          junctionClass: DataClassName.ACTIVITY_CONTACT_RELATION,
          parentFilterKey: 'contact',
          parentFilterExtra: { contactEntity: DataClassName.COMPANY },
          idField: 'activity',
        },
        defaultOrder: [['beginAt', 'desc']],
        summaryLimit: 50,
      },
      tickets: {
        dataClass: DataClassName.TICKET,
        filterKey: 'customer',
        summaryLimit: 10,
      },
      persons: {
        dataClass: DataClassName.PERSON,
        // The Persons tab on a Company detail page reads ContactContactRelation
        // (`father` = the company, `contact` = the person, `contactEntity` = Person)
        // — getRelationDataClass / getSubjectKey / getAdditionalFilters in
        // useDetailsTabData.ts. CompanyPersonRelation backs only the quick-view
        // drawer sublist, so querying it returns a different set than the page shows.
        viaJunction: {
          junctionClass: DataClassName.CONTACT_CONTACT_RELATION,
          parentFilterKey: 'father',
          parentFilterExtra: { contactEntity: DataClassName.PERSON },
          idField: 'contact',
        },
        summaryLimit: 10,
      },
      opportunities: {
        dataClass: DataClassName.OPPORTUNITY,
        filterKey: 'customer',
        defaultOrder: [['updatedAt', 'desc']],
        summaryLimit: 10,
      },
      quotes: {
        dataClass: DataClassName.QUOTE,
        filterKey: 'customer',
        defaultOrder: [['updatedAt', 'desc']],
        summaryLimit: 10,
      },
      orders: {
        dataClass: DataClassName.ORDER,
        filterKey: 'customer',
        defaultOrder: [['updatedAt', 'desc']],
        summaryLimit: 10,
      },
      documents: {
        dataClass: DataClassName.DOCUMENT,
        viaJunction: {
          junctionClass: DataClassName.CONTACT_DOCUMENT_RELATION,
          parentFilterKey: 'contact',
          idField: 'document',
        },
        defaultOrder: [['createdAt', 'desc']],
        summaryLimit: 5,
      },
    },
    fetchHints: {
      note: '"Creation date" (createdAt) is the system-set date the record was created; "Contact since" (since) is a user-editable field meaning when contact was first established — do not confuse them.',
    },
  },

  [DataClassName.PERSON]: {
    aliases: ['contact'],
    searchable: true,
    defaultOrder: [['updatedAt', 'desc']],
    write: { create: true, update: true },
    requiredFlag: 'contacts',

    relations: {
      companies: {
        dataClass: DataClassName.COMPANY,
        // Mirror of Company.persons — the Companies tab on a Person detail page
        // reads ContactContactRelation (`father` = the person, `contact` = the
        // company, `contactEntity` = Company), not CompanyPersonRelation.
        viaJunction: {
          junctionClass: DataClassName.CONTACT_CONTACT_RELATION,
          parentFilterKey: 'father',
          parentFilterExtra: { contactEntity: DataClassName.COMPANY },
          idField: 'contact',
        },
        summaryLimit: 10,
      },
      persons: {
        dataClass: DataClassName.PERSON,
        // Contacts tab on a Person detail page — related persons via
        // ContactContactRelation (`father`/`contact` are the two persons,
        // `contactEntity` = Person). Symmetric like Company.persons: rows may be
        // stored in either direction, so get_related's reversal picks the side
        // that is not this person (mirrors getRelatedContact).
        viaJunction: {
          junctionClass: DataClassName.CONTACT_CONTACT_RELATION,
          parentFilterKey: 'father',
          parentFilterExtra: { contactEntity: DataClassName.PERSON },
          idField: 'contact',
        },
        summaryLimit: 10,
      },
      activities: {
        dataClass: DataClassName.ACTIVITY,
        viaJunction: {
          junctionClass: DataClassName.ACTIVITY_CONTACT_RELATION,
          parentFilterKey: 'contact',
          parentFilterExtra: { contactEntity: DataClassName.PERSON },
          idField: 'activity',
        },
        defaultOrder: [['beginAt', 'desc']],
        summaryLimit: 50,
      },
      tickets: {
        dataClass: DataClassName.TICKET,
        viaJunction: {
          junctionClass: DataClassName.CONTACT_PROJECT_RELATION,
          parentFilterKey: 'contact',
          parentFilterExtra: { projectEntity: DataClassName.TICKET },
          idField: 'project',
        },
        summaryLimit: 10,
      },
      documents: {
        dataClass: DataClassName.DOCUMENT,
        viaJunction: {
          junctionClass: DataClassName.CONTACT_DOCUMENT_RELATION,
          parentFilterKey: 'contact',
          idField: 'document',
        },
        defaultOrder: [['createdAt', 'desc']],
        summaryLimit: 5,
      },
    },
  },

  [DataClassName.OPPORTUNITY]: {
    aliases: ['deal', 'sales deal'],
    searchable: true,
    defaultOrder: [['updatedAt', 'desc']],
    write: { create: true, update: true },
    requiredFlag: 'opportunities',
    relations: {
      activities: {
        dataClass: DataClassName.ACTIVITY,
        viaJunction: {
          junctionClass: DataClassName.ACTIVITY_PROJECT_RELATION,
          parentFilterKey: 'project',
          idField: 'activity',
        },
        defaultOrder: [['beginAt', 'desc']],
        summaryLimit: 50,
      },
      persons: {
        dataClass: DataClassName.PERSON,
        viaJunction: {
          junctionClass: DataClassName.CONTACT_PROJECT_RELATION,
          parentFilterKey: 'project',
          parentFilterExtra: { contactEntity: DataClassName.PERSON },
          idField: 'contact',
        },
        summaryLimit: 10,
      },
      messages: {
        dataClass: DataClassName.MESSAGE,
        filterKey: 'subjectId',
        summaryLimit: 10,
      },
      documents: {
        dataClass: DataClassName.DOCUMENT,
        viaJunction: {
          junctionClass: DataClassName.DOCUMENT_PROJECT_RELATION,
          parentFilterKey: 'project',
          idField: 'document',
        },
        defaultOrder: [['createdAt', 'desc']],
        summaryLimit: 5,
      },
    },
  },

  [DataClassName.ORDER]: {
    aliases: [],
    searchable: true,
    defaultOrder: [['updatedAt', 'desc']],
    write: { create: true, update: false },
    requiredFlag: 'orders',
    relations: {
      activities: {
        dataClass: DataClassName.ACTIVITY,
        viaJunction: {
          junctionClass: DataClassName.ACTIVITY_PROJECT_RELATION,
          parentFilterKey: 'project',
          idField: 'activity',
        },
        defaultOrder: [['beginAt', 'desc']],
        summaryLimit: 50,
      },
      messages: {
        dataClass: DataClassName.MESSAGE,
        filterKey: 'subjectId',
        summaryLimit: 10,
      },
      documents: {
        dataClass: DataClassName.DOCUMENT,
        viaJunction: {
          junctionClass: DataClassName.DOCUMENT_PROJECT_RELATION,
          parentFilterKey: 'project',
          idField: 'document',
        },
        defaultOrder: [['createdAt', 'desc']],
        summaryLimit: 5,
      },
    },
  },

  [DataClassName.QUOTE]: {
    aliases: [],
    searchable: true,
    defaultOrder: [['updatedAt', 'desc']],
    write: { create: true, update: false },
    requiredFlag: 'quotes',
    relations: {
      activities: {
        dataClass: DataClassName.ACTIVITY,
        viaJunction: {
          junctionClass: DataClassName.ACTIVITY_PROJECT_RELATION,
          parentFilterKey: 'project',
          idField: 'activity',
        },
        defaultOrder: [['beginAt', 'desc']],
        summaryLimit: 50,
      },
      messages: {
        dataClass: DataClassName.MESSAGE,
        filterKey: 'subjectId',
        summaryLimit: 10,
      },
      documents: {
        dataClass: DataClassName.DOCUMENT,
        viaJunction: {
          junctionClass: DataClassName.DOCUMENT_PROJECT_RELATION,
          parentFilterKey: 'project',
          idField: 'document',
        },
        defaultOrder: [['createdAt', 'desc']],
        summaryLimit: 5,
      },
    },
  },

  [DataClassName.TICKET]: {
    aliases: ['support ticket', 'case'],
    searchable: true,
    defaultOrder: [['updatedAt', 'desc']],
    write: { create: true, update: true },
    requiredFlag: 'tickets',
    relations: {
      activities: {
        dataClass: DataClassName.ACTIVITY,
        viaJunction: {
          junctionClass: DataClassName.ACTIVITY_PROJECT_RELATION,
          parentFilterKey: 'project',
          idField: 'activity',
        },
        defaultOrder: [['beginAt', 'desc']],
        summaryLimit: 50,
      },
      messages: {
        dataClass: DataClassName.MESSAGE,
        filterKey: 'subjectId',
        summaryLimit: 10,
      },
      documents: {
        dataClass: DataClassName.DOCUMENT,
        viaJunction: {
          junctionClass: DataClassName.DOCUMENT_PROJECT_RELATION,
          parentFilterKey: 'project',
          idField: 'document',
        },
        defaultOrder: [['createdAt', 'desc']],
        summaryLimit: 5,
      },
    },
  },

  [DataClassName.ACTIVITY]: {
    aliases: ['event', 'appointment', 'task'],
    searchable: true,
    defaultOrder: [['beginAt', 'desc']],
    // `agent` is the assigned agent (owner) of the activity — scope "my activities"
    // by it, the same way List scopes by `responsible` and Timerecord by `user`.
    userScopeFields: ['agent'],
    write: { create: true, update: true },
    requiredFlag: 'activities',
    // beginAt/endAt are conditionally-required as a pair — see FieldGroup. The pair
    // gating is enforced by the prompt rule; get_write_schema surfaces this so the
    // fields read "optional — set both or neither" instead of contradicting the list.
    fieldGroups: [{ requireTogether: ['beginAt', 'endAt'] }],
    relations: {
      companies: {
        dataClass: DataClassName.COMPANY,
        viaJunction: {
          junctionClass: DataClassName.ACTIVITY_CONTACT_RELATION,
          parentFilterKey: 'activity',
          parentFilterExtra: { contactEntity: DataClassName.COMPANY },
          idField: 'contact',
        },
      },
      persons: {
        dataClass: DataClassName.PERSON,
        viaJunction: {
          junctionClass: DataClassName.ACTIVITY_CONTACT_RELATION,
          parentFilterKey: 'activity',
          parentFilterExtra: { contactEntity: DataClassName.PERSON },
          idField: 'contact',
        },
      },
      messages: {
        dataClass: DataClassName.MESSAGE,
        filterKey: 'subjectId',
        summaryLimit: 10,
      },
      followUps: {
        dataClass: DataClassName.ACTIVITY,
        filterKey: 'origin',
        defaultOrder: [['beginAt', 'desc']],
        summaryLimit: 20,
      },
      documents: {
        dataClass: DataClassName.DOCUMENT,
        viaJunction: {
          junctionClass: DataClassName.ACTIVITY_DOCUMENT_RELATION,
          parentFilterKey: 'activity',
          idField: 'document',
        },
        defaultOrder: [['createdAt', 'desc']],
        summaryLimit: 5,
      },
    },
    fetchHints: {
      note: 'get_related takes no filters — it cannot fetch "open" activities. For a summary make ONE get_related call, order beginAt desc, and describe what came back. Never state an open/closed count from it: the result is unfiltered and the status codes do not say which statuses count as open. To count open activities use count_entities with filters { status: "open" }, which resolves the label to every code in that status group.',
    },
  },

  [DataClassName.LEAD]: {
    aliases: ['prospect', 'potential customer'],
    searchable: true,
    defaultOrder: [['updatedAt', 'desc']],
    write: { create: true, update: true },
    requiredFlag: 'leads',

    // RightColumn.tsx returns <></> for Lead — no relation tabs in the UI.
    // Relations omitted until Lead detail page is built and junction filters are verified.
    relations: {},
    fetchHints: {
      note: 'isPerson=true for person leads, isPerson=false for company leads.',
    },
  },

  [DataClassName.DOCUMENT]: {
    aliases: ['file', 'attachment'],
    searchable: true,
    defaultOrder: [['createdAt', 'desc']],
    requiredFlag: 'documents',

    relations: {},
    fetchHints: {
      note: "folder=true means it's a folder, not a file. Documents are typically accessed via relations on other entities — direct search is for finding a specific document by name.",
    },
  },

  [DataClassName.LIST]: {
    aliases: ['segment', 'saved list'],
    searchable: true,
    defaultOrder: [['updatedAt', 'desc']],
    userScopeFields: ['responsible'],
    write: { create: true, update: true },
    // `type` is mandatory + readonly and irreversible after create; a hallucinated
    // `static` would create the wrong (filter-less) list. Force it to `dynamic` so the
    // bot only ever creates filter-backed segments, and default `responsible` to the
    // acting user (mirrors the manual create-segment drawer). Both are injected before
    // field-mapping and omitted from the prompted field list.
    forceAtCreate: { type: 'dynamic', responsible: FORCE_CURRENT_USER },
    requiredFlag: 'segments',

    // Membership is stored as the list's query (dynamic) or a fixed member set
    // (static), not a schema relation — so no relation tabs.
    relations: {},
    fetchHints: {
      filterKeys: ['responsible', 'type', 'entityType', 'status'],
      note: 'Segments are saved lists of Company or Person records. entityType is what the list holds (Company/Person); type is dynamic (query-backed) or static (fixed membership). responsible is the list owner — scope by responsible for "my segments". Order by updatedAt for recently-updated segments. The member (contact) count is NOT stored on the List record (the size field is unpopulated) — to answer how many contacts a segment has, which segments are empty / have no contacts, or any segment-size question, call fetch_segment_member_counts; never filter on size.',
    },
  },

  [DataClassName.TIMERECORD]: {
    aliases: ['time record', 'time entry', 'hours'],
    searchable: false,
    // The date field is `recordedAt` (not `date`) — PISA 422s on `date` as a sort
    // key. Mirrors the manual time-tracking list (useMyTimesData/useAllTimesData).
    defaultOrder: [['recordedAt', 'desc']],
    userScopeFields: ['user'],
    write: { create: false, update: false },
    requiredFlag: 'timeTracking',
    relations: {},
    fetchHints: {
      filterKeys: [
        'user',
        'recordedAt.gte',
        'recordedAt.lt',
        'billable',
        'status',
      ],
      note: 'recordedAt is a date-only field — never ask for a time, only ask for the date. totalDuration is numeric hours (2 hours = 2). Scope by user for "my times". Date range via recordedAt.gte + recordedAt.lt (ISO).',
    },
  },

  [DataClassName.TIMERECORD_TEAM]: {
    aliases: ['team relation', 'leader-member link'],
    searchable: false,
    defaultOrder: [['leader', 'asc']],
    requiredFlag: 'timeTracking',
    relations: {},
    fetchHints: {
      filterKeys: ['leader', 'member'],
      note: 'leader filter is supported. To list all leaders: fetch all rows and dedupe by leader field.',
    },
  },

  [DataClassName.TIMERECORD_UTILIZATION]: {
    aliases: ['utilization', 'capacity', 'working time'],
    searchable: false,
    defaultOrder: [['date', 'asc']],
    requiredFlag: 'timeTracking',
    relations: {},
    fetchHints: {
      filterKeys: ['user', 'date.gte', 'date.lt'],
      note: 'user filter is REQUIRED — this endpoint rejects calls without user. One call per user. Fields: date, workingTime, utilizationTime, utilizationFactor, isAbsent.',
    },
  },

  [DataClassName.TIMERECORD_ORDER]: {
    aliases: ['time order', 'project order'],
    searchable: false,
    defaultOrder: [['name', 'asc']],
    requiredFlag: 'timeTracking',
    relations: {},
    fetchHints: {
      filterKeys: ['accountDirector', 'client', '_search'],
      note: 'Filter by accountDirector to get "my orders". Use the budget relation (one call per order) for revenue/budget/days. Fields: name, client, type.',
    },
  },

  [DataClassName.TIMERECORD_BUDGET]: {
    aliases: ['order budget', 'revenue and days'],
    searchable: false,
    defaultOrder: [['recordedAt', 'asc']],
    requiredFlag: 'timeTracking',
    relations: {},
    fetchHints: {
      filterKeys: ['order', 'recordedAt.gte', 'recordedAt.lt'],
      note: 'order filter is REQUIRED — this endpoint rejects calls without order. One call per order. Fields: revenue, totalBudget, usedBudget, remainingBudget, totalDays, usedDays, remainingDays, recordedDays.',
    },
  },

  [DataClassName.TIMERECORD_CLIENT]: {
    aliases: ['time client'],
    searchable: false,
    defaultOrder: [['name', 'asc']],
    requiredFlag: 'timeTracking',
    relations: {},
    fetchHints: {
      filterKeys: ['user', '_search'],
      note: 'Use _search to resolve a name. Fields: name.',
    },
  },

  [DataClassName.TIMERECORD_USER]: {
    aliases: ['time user', 'employee'],
    searchable: false,
    defaultOrder: [['name', 'asc']],
    requiredFlag: 'timeTracking',
    relations: {},
    fetchHints: {
      filterKeys: ['ids', '_search'],
      note: 'Use _search to resolve a name like "Peter" to an id. Use ids (comma-separated) to batch-resolve. Fields: name.',
    },
  },
}
